import { readSyllable } from "@kensio/pinyinjs";

import { mapLimited } from "./map-limited.js";
import type {
  SheetEntry,
  SheetReading,
  WorksheetEntries,
} from "./worksheet-entries.js";

/**
 * The words a pinyin sheet asks about, and the reading it asks for.
 *
 * A pinyin sheet asks about words rather than characters, because a word's
 * reading is what a learner has to write and a character alone may have
 * several. Every word is looked up in the 词典 and asked about in the reading
 * the entry gives first. A run typed with no spaces between its words has no
 * entry, and its characters are asked about one at a time instead, so a
 * pasted sentence still makes a sheet.
 */

/** One word on the sheet, with its entry and the reading it is asked in. */
export interface PinyinItem {
  readonly entry: SheetEntry;

  /** The entry's first reading, which every part of the sheet asks for. */
  readonly reading: SheetReading;
}

/** What the sheet could and could not find entries for. */
export interface PinyinItems {
  readonly items: readonly PinyinItem[];

  /** Characters with no 词典 entry, which are left off the sheet. */
  readonly missing: readonly string[];

  /** Whether there were more words than the sheet takes. */
  readonly capped: boolean;
}

const han = /\p{Script=Han}/u;

/** The tone marks a syllable can carry, after it has been decomposed. */
const toneMarks = /[̀́̄̌]/gu;

/**
 * A reading in numbered pinyin, the way a learner meets it before the marks.
 *
 * Each syllable loses its mark and takes its tone's number, with 5 for the
 * neutral tone, which is how CC-CEDICT and most textbooks number it. The
 * number is the 词典's tone for the syllable, read off the class its reading
 * is coloured by, so 一 and 不 are numbered as the entry marks them in that
 * word. The ü keeps its dots, since `lu4` and `lü4` are different syllables,
 * and the apostrophe goes, since the space between syllables does its work.
 * @returns The syllables numbered and separated by spaces, such as
 * `yin2 hang2`.
 */
export function numberedPinyin(reading: SheetReading): string {
  // `readingsOf` reads one tone for every syllable, so each has its number.
  return reading.syllables
    .map(
      (syllable, at) =>
        `${syllable
          .normalize("NFD")
          .replace(toneMarks, "")
          .normalize("NFC")
          .replace(/['’]/gu, "")}${String(reading.tones[at]!)}`,
    )
    .join(" ");
}

/** A reading as the entry writes it, marks and any apostrophe included. */
export function markedPinyin(reading: SheetReading): string {
  return reading.syllables.join("");
}

/**
 * A syllable as the letters before the final and the final, the way the
 * initials and finals part splits it.
 *
 * The halves are the syllable's spelling cut in two, so the letters a learner
 * writes into a gap are the letters the gap took out. 久 jiǔ is j and iǔ,
 * although the final is iou underneath, and 去 qù is q and ù.
 *
 * y and w count as initials, as Chinese primary schools teach them among the
 * 声母: 我 wǒ is w and ǒ, 月 yuè is y and uè. A syllable starting with a vowel,
 * such as 安 ān or 二 èr, has an empty initial, and the whole syllable is its
 * final.
 */
export interface SyllableHalves {
  readonly initial: string;
  readonly final: string;
}

/**
 * Cut a syllable into its initial and its final.
 * @param syllable - As the entry writes it, with its mark.
 * @returns The two halves, or `undefined` for something that is not one
 * pinyin syllable, such as the letters of 卡拉OK, which the sheet prints
 * whole.
 */
export function syllableHalves(syllable: string): SyllableHalves | undefined {
  const spelled = syllable.normalize("NFC").replace(/['’]/gu, "");
  const parsed = readSyllable(spelled);
  if (parsed === undefined) {
    return undefined;
  }
  const length =
    parsed.initial.length > 0
      ? parsed.initial.length
      : /^[yw]/iu.test(spelled)
        ? 1
        : 0;

  return { initial: spelled.slice(0, length), final: spelled.slice(length) };
}

/** What one word given turned out to be: entries, or characters with none. */
type Found = SheetEntry | string;

/**
 * Look up each word, once, keeping the order the words were given in.
 * @param words - The sheet's words, as typed or as a List gives them.
 * @param limit - The most words a sheet takes. Words past it are not looked
 * up at all, so a long list of saved words costs no more than a sheet's worth
 * of requests.
 * @param concurrency - How many requests are in flight at once.
 */
export async function pinyinItems(
  words: readonly string[],
  entries: Pick<WorksheetEntries, "entry">,
  limit: number,
  concurrency: number,
): Promise<PinyinItems> {
  const unique = [...new Set(words)];
  const asked = unique.slice(0, limit);
  const found = await mapLimited(asked, concurrency, (word) =>
    lookUp(word, entries),
  );

  const seen = new Set<string>();
  const items: PinyinItem[] = [];
  const missing: string[] = [];
  for (const one of found.flat()) {
    const key = typeof one === "string" ? one : one.word;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    const reading = typeof one === "string" ? undefined : one.readings[0];
    if (typeof one === "string" || reading === undefined) {
      missing.push(key);
    } else if (items.length < limit) {
      items.push({ entry: one, reading });
    }
  }

  return { items, missing, capped: unique.length > limit };
}

/**
 * A word's entry, or each of its characters' entries where the word has none.
 * A character with no entry either comes back as itself.
 */
async function lookUp(
  word: string,
  entries: Pick<WorksheetEntries, "entry">,
): Promise<readonly Found[]> {
  const entry = await entries.entry(word);
  if (entry !== undefined && entry.readings.length > 0) {
    return [entry];
  }
  const characters = Array.from(word).filter((letter) => han.test(letter));
  if (characters.length === 1 && characters[0] === word) {
    return [word];
  }

  return Promise.all(
    characters.map(
      async (character): Promise<Found> =>
        (await entries.entry(character)) ?? character,
    ),
  );
}
